/**
 * Template Variables Processor
 * 
 * Supports the following variables:
 * - {{date}} - Current date (e.g., "Mar 27, 2026")
 * - {{date:format}} - Custom date format (e.g., {{date:YYYY-MM-DD}})
 * - {{time}} - Current time (e.g., "06:30 AM")
 * - {{time:format}} - Custom time format (e.g., {{time:HH:mm}})
 * - {{datetime}} - Full timestamp (e.g., "Mar 27, 2026 at 06:30 AM")
 * - {{title}} - Note title
 * - {{user}} - User email
 * - {{random}} - Random inspirational quote
 */

import { format as formatDate } from 'date-fns';

// Inspirational quotes for {{random}}
const QUOTES = [
  "The only way to do great work is to love what you do. — Steve Jobs",
  "In the middle of difficulty lies opportunity. — Albert Einstein",
  "The best time to plant a tree was 20 years ago. The second best time is now. — Chinese Proverb",
  "It does not matter how slowly you go as long as you do not stop. — Confucius",
  "The secret of getting ahead is getting started. — Mark Twain",
  "Believe you can and you're halfway there. — Theodore Roosevelt",
  "The mind is everything. What you think you become. — Buddha",
  "Strive not to be a success, but rather to be of value. — Albert Einstein",
  "The only impossible journey is the one you never begin. — Tony Robbins",
  "What you get by achieving your goals is not as important as what you become. — Zig Ziglar",
  "Everything you've ever wanted is on the other side of fear. — George Addair",
  "Success is not final, failure is not fatal: it is the courage to continue that counts. — Winston Churchill",
  "The future belongs to those who believe in the beauty of their dreams. — Eleanor Roosevelt",
  "It is during our darkest moments that we must focus to see the light. — Aristotle",
  "Don't watch the clock; do what it does. Keep going. — Sam Levenson",
];

/**
 * Get a random quote
 */
function getRandomQuote() {
  return QUOTES[Math.floor(Math.random() * QUOTES.length)];
}

/**
 * Format date with custom format string
 * Supports: YYYY, MM, DD, HH, mm, ss, etc.
 */
function formatCustomDate(date, formatStr) {
  try {
    return formatDate(date, formatStr);
  } catch {
    return formatDate(date, 'PPP'); // Fallback to default
  }
}

/**
 * Process all template variables in content
 * @param {string} content - Template content with variables
 * @param {object} context - Context object with title, user, etc.
 * @returns {string} - Processed content with variables replaced
 */
export function processTemplateVariables(content, context = {}) {
  if (!content) return content;
  
  const now = new Date();
  const { title = 'Untitled', user = '' } = context;
  
  let result = content;
  
  // {{date}} - Default date format
  result = result.replace(/\{\{date\}\}/gi, formatDate(now, 'PPP')); // Mar 27, 2026
  
  // {{date:FORMAT}} - Custom date format
  result = result.replace(/\{\{date:([^}]+)\}\}/gi, (match, fmt) => {
    return formatCustomDate(now, fmt);
  });
  
  // {{time}} - Default time format
  result = result.replace(/\{\{time\}\}/gi, formatDate(now, 'p')); // 6:30 AM
  
  // {{time:FORMAT}} - Custom time format
  result = result.replace(/\{\{time:([^}]+)\}\}/gi, (match, fmt) => {
    return formatCustomDate(now, fmt);
  });
  
  // {{datetime}} - Full timestamp
  result = result.replace(/\{\{datetime\}\}/gi, formatDate(now, "PPP 'at' p")); // Mar 27, 2026 at 6:30 AM
  
  // {{title}} - Note title
  result = result.replace(/\{\{title\}\}/gi, title);
  
  // {{user}} - User email
  result = result.replace(/\{\{user\}\}/gi, user);
  
  // {{random}} - Random inspirational quote
  result = result.replace(/\{\{random\}\}/gi, getRandomQuote());
  
  return result;
}

/**
 * Available template variables with descriptions
 */
export const TEMPLATE_VARIABLES = [
  { variable: '{{date}}', description: 'Current date (Mar 27, 2026)', example: 'Mar 27, 2026' },
  { variable: '{{date:YYYY-MM-DD}}', description: 'Custom date format', example: '2026-03-27' },
  { variable: '{{time}}', description: 'Current time (6:30 AM)', example: '6:30 AM' },
  { variable: '{{time:HH:mm}}', description: 'Custom time format', example: '06:30' },
  { variable: '{{datetime}}', description: 'Full timestamp', example: 'Mar 27, 2026 at 6:30 AM' },
  { variable: '{{title}}', description: 'Note title', example: 'My Note Title' },
  { variable: '{{user}}', description: 'Your email', example: 'you@example.com' },
  { variable: '{{random}}', description: 'Random inspirational quote', example: '"Believe you can..." — T.R.' },
];

/**
 * Insert a variable at cursor position in a text input/textarea
 * @param {HTMLElement} element - Input or textarea element
 * @param {string} variable - Variable to insert
 * @param {function} setValue - State setter function
 */
export function insertVariableAtCursor(element, variable, setValue) {
  if (!element) return;
  
  const start = element.selectionStart;
  const end = element.selectionEnd;
  const value = element.value;
  
  const newValue = value.substring(0, start) + variable + value.substring(end);
  setValue(newValue);
  
  // Set cursor position after the inserted variable
  setTimeout(() => {
    element.selectionStart = element.selectionEnd = start + variable.length;
    element.focus();
  }, 0);
}

export default { processTemplateVariables, TEMPLATE_VARIABLES, insertVariableAtCursor };
